import { apiClient } from './apiClient';

// Test API connection
export const testApiConnection = async () => {
  try {
    console.log('Testing API connection');
    const response = await apiClient.get('/api/test');
    console.log('API test response:', response);
    return response;
  } catch (error) {
    console.error('API connection test failed:', error);
    throw error;
  }
};

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  user?: {
    email: string;
    fullName: string;
  };
  token?: string;
}

interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface RegisterResponse {
  success: boolean;
  message: string;
  user?: {
    email: string;
    fullName: string;
  };
}

interface ForgotPasswordRequest {
  email: string;
}

interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

interface ValidateResetTokenResponse {
  valid: boolean;
  message?: string;
}

interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export const login = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    console.log('Attempting login for:', email);
    const response = await apiClient.post('/api/auth/login', {
      email,
      password
    });

    console.log('Login response:', response);

    return {
      success: true,
      message: 'Login successful',
      user: response.user,
      token: response.token
    };
  } catch (error: any) {
    console.error('Login error:', error);
    if (error.response) {
      const status = error.response?.status;
      const message = error.response?.data?.message;

      if (status === 401) {
        throw new Error('Invalid email or password');
      } else if (status === 400) {
        throw new Error(message || 'Invalid input data');
      } else if (status === 429) {
        throw new Error('Too many login attempts. Please try again later');
      } else {
        throw new Error(message || 'Login failed. Please try again');
      }
    }
    throw new Error('Login failed. Please try again');
  }
};

export const register = async (firstName: string, lastName: string, email: string, password: string): Promise<RegisterResponse> => {
  try {
    console.log('Attempting registration for:', email);
    const response = await apiClient.post('/api/auth/register', {
      firstName,
      lastName,
      email,
      password
    });

    console.log('Registration response:', response);

    return {
      success: true,
      message: 'Registration successful',
      user: response.user
    };
  } catch (error: any) {
    console.error('Registration error:', error);
    if (error.response) {
      const status = error.response?.status;
      const message = error.response?.data?.message;

      if (status === 409) {
        throw new Error('An account with this email already exists');
      } else if (status === 400) {
        throw new Error(message || 'Invalid input data');
      } else if (status === 422) {
        throw new Error(message || 'Password does not meet requirements');
      } else {
        throw new Error(message || 'Registration failed. Please try again');
      }
    }
    throw new Error('Registration failed. Please try again');
  }
};

export const requestPasswordReset = async (email: string): Promise<ForgotPasswordResponse> => {
  try {
    console.log('Requesting password reset for:', email);

    const response = await apiClient.post('/api/auth/forgot-password', { email });
    console.log('Password reset response:', response);

    return {
      success: true,
      message: 'If an account exists for this email, you will receive a password reset link.'
    };
  } catch (error: any) {
    console.error('Error requesting password reset:', error);
    // Always return success to avoid leaking user existence
    return {
      success: true,
      message: 'If an account exists for this email, you will receive a password reset link.'
    };
  }
};

export const validateResetToken = async (token: string): Promise<ValidateResetTokenResponse> => {
  try {
    console.log('Validating reset token');
    const response = await apiClient.get(`/api/auth/validate-reset-token?token=${token}`);
    console.log('Token validation response:', response);

    return {
      valid: response.valid,
      message: response.message
    };
  } catch (error: any) {
    console.error('Error validating reset token:', error);
    if (error.response) {
      const status = error.response?.status;
      if (status === 400 || status === 401) {
        return {
          valid: false,
          message: 'Token expired or invalid.'
        };
      }
    }
    throw new Error('Failed to validate reset token');
  }
};

export const resetPassword = async (token: string, newPassword: string): Promise<ResetPasswordResponse> => {
  try {
    console.log('Resetting password');
    const response = await apiClient.post('/api/auth/reset-password', {
      token,
      newPassword
    });

    return {
      success: true,
      message: 'Password has been reset successfully.'
    };
  } catch (error: any) {
    console.error('Error resetting password:', error);
    if (error.response) {
      const status = error.response?.status;
      const message = error.response?.data?.message;

      if (status === 400) {
        throw new Error(message || 'Invalid password format');
      } else if (status === 401) {
        throw new Error('Invalid or expired reset token');
      } else {
        throw new Error(message || 'Failed to reset password');
      }
    }
    throw new Error('Failed to reset password');
  }
}; 